import { getAssignedDataGroup } from "../../../core/router/auth";
import { ResourceName, ResourceVariant } from "../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../core/router/types/routerTypes";
import type { FormField } from "../types/types";
import { FormComponent } from "../types/types";

const APAC_FORM_CONFIG_NAME = "APAC";

// Fields that link to another entity's own edit page (AP3ADMIN-5014), keyed by field name.
const LINKED_ENTITY_ROUTES: Record<string, ResourceName> = {
    terminalProfileId: ResourceName.TERMINAL_PROFILES,
    siteId: ResourceName.SITES,
    appProfileListId: ResourceName.APPLICATION_PROFILE_LISTS,
    parentUnit: ResourceName.SITE_GROUPS,
    terminalTypeId: ResourceName.TERMINAL_TYPES,
    posTypeId: ResourceName.POS_TYPES,
    posMappingId: ResourceName.POS_MAPPINGS,
    cardImplementationListId: ResourceName.CARD_IMPLEMENTATIONS,
    hostConfigId: ResourceName.HOST_CONFIGURATIONS,
    hostResponseCodeId: ResourceName.HOST_RESPONSE_CODE,
    dccProfileId: ResourceName.DCC_PROFILES,
    dialogProfileId: ResourceName.DIALOG_PROFILES,
    printingProfileId: ResourceName.PRINTING_PROFILES,
    allowedProductsProfileId: ResourceName.ALLOWED_PRODUCT_PROFILES,
    cardTypeId: ResourceName.CARD_TYPES,
    productProfileId: ResourceName.PRODUCT_PROFILES,
    childProductProfileId: ResourceName.PRODUCT_PROFILES,
    promptDataProfileId: ResourceName.PROMPT_DATA_PROFILES,
    contractProfileId: ResourceName.CONTRACT_PROFILES,
    vatCode: ResourceName.VAT_CODES,
    // templateDataGroup: ResourceName.DATA_GROUPS,
};

// Resources whose edit page needs the entity's own id as a `?id=` query param (not just the :id path param)
const RESOURCES_NEEDING_ID_QUERY_PARAM: ReadonlySet<ResourceName> = new Set([ResourceName.SITE_GROUPS, ResourceName.SITES]);

// Acquirer route splits on Physical/Virtual (routes.ts:52-55); discriminator=null forces Physical,
// used for dccServiceProviderForeignKeyId which always resolves to a Physical Acquirer.
const buildAcquirerIdHref = (discriminator: string | null, value: string): string => {
    const isVirtual = discriminator === "VIRTUAL";
    const variant = isVirtual ? ResourceVariant.VIRTUAL : ResourceVariant.PHYSICAL;
    const acquirerIdQueryParam = isVirtual ? "" : `?acquirerId=${value}`;
    return `${BASE_PATH}/${ResourceName.ACQUIRER}/${variant}/${FORM_ACTION.EDIT}/${value}${acquirerIdQueryParam}`;
};

const buildLinkedEntityHref = (resource: ResourceName, value: string): string =>
    `${BASE_PATH}/${resource}/${FORM_ACTION.EDIT}/${value}${RESOURCES_NEEDING_ID_QUERY_PARAM.has(resource) ? `?id=${value}` : ""}`;

// Every tenant is exclusively APAC or non-APAC, so the current dataGroup decides the edit route variant.
const buildEmvKernelProfileHref = (value: string): string => {
    const isApac = getAssignedDataGroup()?.formConfigName?.toUpperCase() === APAC_FORM_CONFIG_NAME;
    return `${BASE_PATH}/${ResourceName.EMV_KERNEL_PROFILES}${isApac ? `/${ResourceVariant.APAC}` : ""}/${FORM_ACTION.EDIT}/${value}`;
};

// Returns a builder for the field's entity-link href, or undefined if the field doesn't link anywhere.
export const getFieldLinkHrefBuilder = (field: FormField, searchParams: URLSearchParams): ((value: string) => string) | undefined => {
    if (field.component !== FormComponent.SELECT_DYNAMIC) {
        return undefined;
    }
    if (field.name === "acquirerId") {
        return (value: string) => buildAcquirerIdHref(searchParams.get("discriminator"), value);
    }
    if (field.name === "dccServiceProviderForeignKeyId") {
        return (value: string) => buildAcquirerIdHref(null, value);
    }
    if (field.name === "defaultEmvKernelId" || field.name === "emvKernelConfigurationId") {
        return buildEmvKernelProfileHref;
    }
    const linkResource = LINKED_ENTITY_ROUTES[field.name];
    return linkResource ? (value: string) => buildLinkedEntityHref(linkResource, value) : undefined;
};
